import { useState, useRef, useEffect, useLayoutEffect, ReactNode } from 'react';

export interface PillTabItem {
    key: string;
    label: ReactNode;
    icon?: ReactNode;
}

/**
 * Reusable pill tabs with a smooth sliding gradient indicator.
 * The active look is drawn by a single absolutely-positioned `.salary-pill-indicator`
 * that slides/resizes between tabs (CSS can't tween a gradient background).
 * Styles live in scss/components/salary-tabs.style.scss.
 */
export const PillTabs = ({
    tabs,
    activeKey,
    onChange,
    className = '',
}: {
    tabs: PillTabItem[];
    activeKey: string;
    onChange: (key: string) => void;
    className?: string;
}) => {
    const tabsRef = useRef<HTMLDivElement>(null);
    const [indicator, setIndicator] = useState<{ left: number; width: number }>({ left: 0, width: 0 });
    const [ready, setReady] = useState(false);

    // Position the indicator over the active tab before paint (no flash / slide-in).
    useLayoutEffect(() => {
        const measure = () => {
            const container = tabsRef.current;
            if (!container) return;
            const activeBtn = container.querySelector<HTMLElement>('.salary-pill-tab.active');
            if (activeBtn) {
                setIndicator({ left: activeBtn.offsetLeft, width: activeBtn.offsetWidth });
            }
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [activeKey, tabs.length]);

    // Enable the slide transition only after the first positioning.
    useEffect(() => {
        const id = requestAnimationFrame(() => setReady(true));
        return () => cancelAnimationFrame(id);
    }, []);

    return (
        <div className={`salary-pill-tabs ${className}`} role="tablist" ref={tabsRef}>
            <span
                className={`salary-pill-indicator ${ready ? 'is-animated' : ''}`}
                aria-hidden="true"
                style={{ transform: `translateX(${indicator.left}px)`, width: indicator.width }}
            />
            {tabs.map(({ key, label, icon }) => (
                <button
                    key={key}
                    type="button"
                    role="tab"
                    aria-selected={activeKey === key}
                    className={`salary-pill-tab ${activeKey === key ? 'active' : ''}`}
                    onClick={() => onChange(key)}
                >
                    {icon}
                    <span>{label}</span>
                </button>
            ))}
        </div>
    );
};
